import { ChartDataOptions, ChartType } from '@sisense/sdk-ui';
import {
	AxesMapping,
	getMinimalChartStyleOptions,
} from '@sisense/sdk-ui/analytics-composer';
import {
	ExtendedChartWidgetProps,
	BuildChartContext,
	updateChartTypeSchema,
} from '../types';
import { generateChartRecommendations } from '../create-chart/generate-chart-recommendations';
import z from 'zod';
import { devDebug } from '@/utils/dev-debug';

type UpdateChartTypeHelperParams = {
	userPrompt: string;
	chartType: ChartType;
	chartWidgetProps: ExtendedChartWidgetProps;
};

export const updateChartTypeHelper = async ({
	userPrompt,
	chartType,
	chartWidgetProps,
}: UpdateChartTypeHelperParams) => {
	const { dataColumns, styleOptions: existingStyleOptions } =
		chartWidgetProps;

	const chartRecommendations = await generateChartRecommendations(
		userPrompt,
		dataColumns,
		chartType,
	);

	const { axesMapping, chartType: newChartType } = chartRecommendations;

	chartWidgetProps.dataOptions = axesMapping as ChartDataOptions;
	chartWidgetProps.chartType = newChartType;

	const baseStyleOptions = getMinimalChartStyleOptions(
		newChartType,
		chartWidgetProps.dataOptions as AxesMapping,
	);

	const styleOptions = existingStyleOptions
		? {
				...baseStyleOptions,
				// Preserve specific properties from existingStyleOptions
				...('legend' in existingStyleOptions &&
				'legend' in baseStyleOptions
					? { legend: existingStyleOptions.legend }
					: {}),
				...('labels' in existingStyleOptions &&
				'labels' in baseStyleOptions
					? { labels: existingStyleOptions.labels }
					: {}),
				...('xAxis' in existingStyleOptions &&
				'xAxis' in baseStyleOptions
					? { xAxis: existingStyleOptions.xAxis }
					: {}),
				...('yAxis' in existingStyleOptions &&
				'yAxis' in baseStyleOptions
					? { yAxis: existingStyleOptions.yAxis }
					: {}),
				...('y2Axis' in existingStyleOptions &&
				'y2Axis' in baseStyleOptions
					? { y2Axis: existingStyleOptions.y2Axis }
					: {}),
				...('dataLimits' in existingStyleOptions &&
				'dataLimits' in baseStyleOptions
					? { dataLimits: existingStyleOptions.dataLimits }
					: {}),
				...('markerSize' in existingStyleOptions &&
				'markerSize' in baseStyleOptions
					? { markerSize: existingStyleOptions.markerSize }
					: {}),
		  }
		: baseStyleOptions;

	chartWidgetProps.styleOptions = styleOptions;

	return chartWidgetProps;
};

export const updateChartType = async (
	{ chartId, userPrompt, chartType }: z.infer<typeof updateChartTypeSchema>,
	context: BuildChartContext,
) => {
	devDebug('toolCall', {
		tool: 'updateChartType',
		chartId,
		userPrompt,
		chartType,
	});
	const chartWidgetProps = context.retrieveChart?.(chartId);
	if (!chartWidgetProps) {
		throw new Error(`Chart not found`);
	}

	const updatedChartWidgetProps = await updateChartTypeHelper({
		userPrompt,
		chartType,
		chartWidgetProps,
	});
	context.saveChart?.(chartId, updatedChartWidgetProps);
	return {
		message: 'Chart type updated successfully',
	};
};
